import { acceptHMRUpdate, defineStore } from 'pinia'
import type {AxiosError, AxiosInstance} from 'axios'
import { API } from '../services';
import { getAuth } from 'firebase/auth';
import { errorTracker } from '../services/error/errorTracking';
import type { AgencyProfile } from '../types/business';

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance
    catTags: string[]
  }
}

const ACTIVE_BRAND_STORAGE_KEY = 'geo:activeBrandId';

type Brand = { id: number; name?: string; landingUrl?: string; [key: string]: unknown };

type BrandLimit = {
  maxBrands: number;
  currentBrands: number;
  canAddBrand: boolean;
};

const useBusinessStore = defineStore('business', {
  state: () => ({
    error: undefined as unknown,
    business: null as any,
    isLoading: false,
    brands: [] as Brand[],
    activeBrandId: null as number | null,
    brandLimit: null as BrandLimit | null,
  }),
  getters: {
    hasBusiness: (state) => !!state.business,
    businessType: (state) => state.business?.type,
    activeBrand: (state) => {
      if (!state.brands?.length) return null;
      if (state.activeBrandId != null) {
        const match = state.brands.find((b) => b.id === state.activeBrandId);
        if (match) return match;
      }
      return state.brands[0] ?? null;
    },
    primaryBrand: (state) => state.brands[0] ?? null,
  },
  actions: {
    async createPartialBusiness(type: string) {
      try {
        const user = getAuth().currentUser;
        const firebaseUserUuid = user?.uid;
        if (!firebaseUserUuid) {
            throw new Error('User is not authenticated.');
        }

        const response = await API.business.createPartialBusiness(type)
        const success = response.status === 200;
        if (success) {
          this.business = response.data.business;
          return { 
            success: true, 
            response: response.data 
          };
        }
        
        return {
          success: success
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'createPartialBusiness'
          });
        const _error = error as AxiosError<string>; 
        return {
          success: false,
          status: _error.response?.status
        };
      }
    },

    async getUserBusiness(userId: number) {
      try {
        this.isLoading = true;
        const response = await API.business.getUserBusiness(userId);

        console.log('response correct?', response);
        if (response.status === 200) {
          this.business = response.data.business;
          return { 
            success: true, 
            business: response.data.business 
          };
        }

        throw new Error('Something went wrong');
      } catch (error) {
        // If 404, it means no business exists, which is not an error
        const _error = error as AxiosError<string>;
        if (_error.response?.status === 404) {
          console.log('404 from catch handler will run');
          this.business = null;
          return {
            success: true,
            business: null
          };
        }
        console.log('404 handler will not run');

        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'getUserBusiness'
          });
        
        return {
          success: false,
          status: _error.response?.status
        };
      } finally {
        this.isLoading = false;
      }
    },

    hydrateActiveBrandIdFromStorage() {
      const raw = localStorage.getItem(ACTIVE_BRAND_STORAGE_KEY);
      if (raw) {
        const parsed = parseInt(raw, 10);
        if (!Number.isNaN(parsed)) {
          this.activeBrandId = parsed;
        }
      }
    },

    setActiveBrandId(brandId: number | null) {
      this.activeBrandId = brandId;
      if (brandId == null) {
        localStorage.removeItem(ACTIVE_BRAND_STORAGE_KEY);
      } else {
        localStorage.setItem(ACTIVE_BRAND_STORAGE_KEY, String(brandId));
      }
    },

    async getBrandByBusinessId(businessId: number) {
      try {
        this.hydrateActiveBrandIdFromStorage();
        const response = await API.business.getBrandByBusinessId(businessId);
        const success = response.status === 200;
        
        if (success) {
          const brands = response.data.brands || [];
          this.brands = brands;
          void this.fetchBrandLimit();

          if (
            this.activeBrandId != null &&
            brands.length > 0 &&
            !brands.some((b: Brand) => b.id === this.activeBrandId)
          ) {
            this.setActiveBrandId(brands[0].id);
          }

          return { 
            success: true, 
            brand: this.activeBrand,
            brands: brands,
            count: response.data.count || brands.length
          };
        }
        
        return {
          success: false,
          status: response.status
        };
      } catch (error) {
        // If 404, it means no business/brands exist, which is not an error
        const _error = error as AxiosError<string>;
        if (_error.response?.status === 404) {
          return {
            success: true,
            brand: null,
            brands: [],
            count: 0
          };
        }

        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'getBrandByBusinessId'
          });
        
        return {
          success: false,
          status: _error.response?.status
        };
      }
    },

    async fetchBrandLimit() {
      try {
        const response = await API.business.getBrandLimit();
        if (response.status === 200) {
          this.brandLimit = {
            maxBrands: response.data.maxBrands,
            currentBrands: response.data.currentBrands,
            canAddBrand: response.data.canAddBrand,
          };
          return { success: true, brandLimit: this.brandLimit };
        }
        return { success: false, status: response.status };
      } catch (error) {
        errorTracker.captureError(error as Error, {
          componentName: 'BusinessStore',
          action: 'fetchBrandLimit',
        });
        return { success: false };
      }
    },

    async createBrand(businessId: number, brandData: { name: string; landingUrl: string }) {
      try {
        const response = await API.business.createBrand(businessId, brandData);
        const success = response.status === 201; // 201 for creation
        
        if (success) {
          if (response.data.brand) {
            this.brands.push(response.data.brand);
          }
          await this.fetchBrandLimit();
          return { 
            success: true, 
            brand: response.data.brand 
          };
        }
        
        return {
          success: false,
          status: response.status,
          error: response.data?.error
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'createBrand'
          });
        const _error = error as AxiosError<{ error?: string }>; 
        return {
          success: false,
          status: _error.response?.status,
          error: _error.response?.data?.error || 'Failed to create brand'
        };
      }
    },

    async updateAgencyProfile(businessId: number, profile: AgencyProfile) {
      try {
        this.isLoading = true;
        const response = await API.business.updateAgencyProfile(businessId, profile);
        const success = response.status === 200;
        
        if (success) {
          if (this.business) {
            this.business.name = profile.name;
            this.business.landingUrl = profile.landingUrl;
          }
          
          return { 
            success: true, 
            business: response.data.business 
          };
        }
        
        return {
          success: false,
          status: response.status,
          error: response.data?.error
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'updateAgencyProfile'
          });
        const _error = error as AxiosError<{ error?: string }>; 
        return {
          success: false,
          status: _error.response?.status,
          error: _error.response?.data?.error || 'Failed to update agency profile'
        };
      } finally {
        this.isLoading = false;
      }
    },

    async updateBrand(businessId: number, brandId: number, brandData: { name: string; landingUrl?: string }) {
      try {
        const response = await API.business.updateBrand(businessId, brandId, brandData);
        const success = response.status === 200;
        
        if (success) {
          const updated = response.data.brand;
          if (updated) {
            const index = this.brands.findIndex((b) => b.id === brandId);
            if (index !== -1) {
              this.brands[index] = { ...this.brands[index], ...updated };
            }
          }
          return { 
            success: true, 
            brand: updated 
          };
        }
        
        return {
          success: false,
          status: response.status,
          error: response.data?.error
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'updateBrand'
          });
        const _error = error as AxiosError<{ error?: string }>; 
        return {
          success: false,
          status: _error.response?.status,
          error: _error.response?.data?.error || 'Failed to update brand'
        };
      }
    },

    async removeBrand(businessId: number, brandId: number) {
      try {
        const response = await API.business.deleteBrand(businessId, brandId);
        const success = response.status === 200;

        if (success) {
          this.brands = this.brands.filter((b) => b.id !== brandId);
          if (this.activeBrandId === brandId) {
            this.setActiveBrandId(this.brands[0]?.id ?? null);
          }
          await this.fetchBrandLimit();
          return { success: true };
        }

        return {
          success: false,
          status: response.status,
          error: response.data?.error
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'removeBrand'
          });
        const _error = error as AxiosError<{ error?: string }>;
        return {
          success: false,
          status: _error.response?.status,
          error: _error.response?.data?.error || 'Failed to delete brand'
        };
      }
    },

    async checkBrandJobStatus(brandId: number, jobType: 'fetch_pages' | 'generate_prompts' | 'run_prompts') {
      try {
        const response = await API.business.checkBrandJobStatus(brandId, jobType);
        const success = response.status === 200;
        
        if (success) {
          return { 
            success: true, 
            jobStatus: response.data 
          };
        }
        
        return {
          success: false,
          status: response.status
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'checkBrandJobStatus'
          });
        const _error = error as AxiosError<string>; 
        return {
          success: false,
          status: _error.response?.status,
          message: _error.response?.data || 'Failed to check job status'
        };
      }
    },

    async triggerDomainScan(brandId: number) {
      try {
        const response = await API.business.triggerDomainScan(brandId);
        const success = response.status === 200;
        
        if (success) {
          return { 
            success: true, 
            data: response.data 
          };
        }
        
        // 400 means a job is currently running - only treat as success if RUNNING
        if (response.status === 400) {
          const jobStatus = (response.data as any)?.jobStatus;
          if (jobStatus === 'RUNNING') {
            return {
              success: true,
              status: response.status,
              message: 'Job already running',
              jobExists: true
            };
          }
          return {
            success: false,
            status: response.status,
            message: `Previous job status: ${jobStatus || 'unknown'}`
          };
        }
        
        return {
          success: false,
          status: response.status
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'triggerDomainScan'
          });
        const _error = error as AxiosError<string>; 
        
        // 400 means a job is currently running - only treat as success if RUNNING
        if (_error.response?.status === 400) {
          const jobStatus = (_error.response?.data as any)?.jobStatus;
          if (jobStatus === 'RUNNING') {
            return {
              success: true,
              status: 400,
              message: 'Job already running',
              jobExists: true
            };
          }
          return {
            success: false,
            status: 400,
            message: `Previous job status: ${jobStatus || 'unknown'}`
          };
        }
        
        return {
          success: false,
          status: _error.response?.status,
          message: _error.response?.data || 'Failed to trigger domain scan'
        };
      }
    },

    async getBrandPages(brandId: number) {
      try {
        const response = await API.business.getBrandPages(brandId);
        const success = response.status === 200;

        if (success) {
          return {
            success: true,
            data: response.data
          };
        }

        return {
          success: false,
          status: response.status
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'getBrandPages'
          });
        const _error = error as AxiosError<string>;
        return {
          success: false,
          status: _error.response?.status,
          message: _error.response?.data || 'Failed to get brand pages'
        };
      }
    },

    async getLastAnalysisRunDate(brandId: number) {
      try {
        const response = await API.business.getLastAnalysisRunDate(brandId);
        return response;
      } catch (error) {
        errorTracker.captureError(error as Error, {
            componentName: 'BusinessStore',
            action: 'getLastAnalysisRunDate'
          });
        const _error = error as AxiosError<string>; 
        return {
          status: _error.response?.status || 500,
          data: {
            success: false,
            error: _error.response?.data || 'Failed to get last analysis run date'
          }
        };
      }
    },

    // Clear business store state (useful during logout)
    clearState() {
      this.business = null;
      this.error = undefined;
      this.isLoading = false;
      this.brands = [];
      this.activeBrandId = null;
      localStorage.removeItem(ACTIVE_BRAND_STORAGE_KEY);
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useBusinessStore, import.meta.hot))

export default useBusinessStore
